import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useSelector } from 'react-redux';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
  padding: 30px;
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 960px;
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const Section = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SectionHeader = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
`;

const SectionDescription = styled.p`
  margin: 8px 0 0;
  color: #6b7280;
  font-size: 14px;
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 16px;
  align-items: ${({ $top }) => ($top ? 'flex-start' : 'center')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
  text-align: right;
  padding-top: ${({ $top }) => ($top ? '8px' : '0')};

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: #ffffff;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
  }

  &[readonly] {
    background: #f9fafb;
    cursor: default;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Textarea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  min-height: ${({ $large }) => ($large ? '260px' : '160px')};
  resize: vertical;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const KeywordInputRow = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $secondary }) => ($secondary ? '#6b7280' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)')};
  color: #ffffff;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
  height: 64px;
  font-size: 18px;
  margin-top: 10px;
`;

const KeywordList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const KeywordItem = styled.span`
  background: #1f2937;
  color: #ffffff;
  padding: 10px 14px;
  border-radius: 999px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #111827;
  }
`;

const PreviewWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const PreviewCard = styled.div`
  position: relative;
  width: 160px;
  padding-bottom: 160px;
  background: #f9fafb;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:hover::after {
    content: '삭제';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
  }
`;

const PreviewImage = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  top: 0;
  left: 0;
`;

const MultiPreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
`;

const HiddenInput = styled.input`
  display: none;
`;

function ExtensionPost() {
  const token = useSelector((state) => state.auth.token);

  const [extensionType, setExtensionType] = useState('free');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [keywordText, setKeywordText] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [thumbnailImage, setThumbnailImage] = useState(null);
  const [detailImages, setDetailImages] = useState([]);

  const [extensionId, setExtensionId] = useState('');
  const [codeType, setCodeType] = useState('free');
  const [codeName, setCodeName] = useState('');
  const [extensionCode, setExtensionCode] = useState('');

  const thumbnailInputRef = useRef(null);
  const imagesInputRef = useRef(null);

  const handleAddKeyword = () => {
    if (!keywordText.trim()) return;
    if (keywords.includes(keywordText.trim())) return;
    setKeywords(prev => [...prev, keywordText.trim()]);
    setKeywordText('');
  };

  const handleRemoveKeyword = (value) => {
    setKeywords(prev => prev.filter(item => item !== value));
  };

  const handleThumbnailSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setThumbnailImage({
        preview: reader.result,
        file,
        name: file.name,
      });
    };
    reader.readAsDataURL(file);
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }
  };

  const handleRemoveThumbnail = () => {
    setThumbnailImage(null);
  };

  const handleImagesSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setDetailImages(prev => [...prev, { preview: reader.result, file, name: file.name }]);
    };
    reader.readAsDataURL(file);

    if (imagesInputRef.current) {
      imagesInputRef.current.value = '';
    }
  };

  const handleRemoveDetailImage = (index) => {
    setDetailImages(prev => prev.filter((_, i) => i !== index));
  };

  const submitExtension = async () => {
    const requestBody = {
      extension_type: extensionType,
      title,
      content,
      keyword: keywords,
      video_url: videoUrl,
    };

    const formData = new FormData();
    formData.append('req_body', JSON.stringify(requestBody));

    if (thumbnailImage?.file) {
      formData.append('thumbnail_image', thumbnailImage.file);
    }

    if (detailImages.length > 0) {
      detailImages.forEach((image) => {
        if (image.file) {
          formData.append('images', image.file);
        }
      });
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/extension`, formData, {
        headers: {
          'access-token': token,
        },
      });
      setExtensionId(response.data.id);
      alert('익스텐션이 등록되었습니다.');
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.detail || '익스텐션 추가에 실패했습니다.');
    }
  };

  const submitCode = async () => {
    const requestBody = {
      extension_type: codeType,
      name: codeName,
      code: extensionCode,
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/code?extension_id=${extensionId}`, requestBody, {
        headers: {
          'access-token': token,
        },
      });
      setCodeType('free');
      setExtensionId('');
      setCodeName('');
      setExtensionCode('');
      alert('코드가 추가되었습니다.');
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.detail || '코드 추가에 실패했습니다.');
    }
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <Section>
          <SectionHeader>
            <SectionTitle>익스텐션 등록</SectionTitle>
            <SectionDescription>확장 프로그램 기본 정보와 파일을 업로드합니다.</SectionDescription>
          </SectionHeader>

          <FormGrid>
            <FormRow>
              <Label htmlFor="extensionType">익스텐션 타입</Label>
              <Select id="extensionType" value={extensionType} onChange={(e) => setExtensionType(e.target.value)}>
                <option hidden>선택</option>
                <option value="free">무료</option>
                <option value="block">블록</option>
                <option value="timer">타이머</option>
                <option value="edit">입력폼</option>
                <option value="popup">팝업</option>
                <option value="floating">플로팅 버튼</option>
                <option value="rolling_banner">롤링 배너</option>
              </Select>
            </FormRow>

            <FormRow>
              <Label htmlFor="extensionTitle">익스텐션 타이틀</Label>
              <Input
                id="extensionTitle"
                placeholder="익스텐션 제목을 입력하세요"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormRow>

            <FormRow $top>
              <Label htmlFor="extensionContent" $top>익스텐션 내용</Label>
              <Textarea
                id="extensionContent"
                placeholder="익스텐션 내용을 입력하세요"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </FormRow>

            <FormRow>
              <Label htmlFor="videoUrl">소개 영상 URL</Label>
              <Input
                id="videoUrl"
                placeholder="소개 영상(선택) URL을 입력하세요"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
              />
            </FormRow>

            <FormRow>
              <Label htmlFor="keywordInput">검색 키워드</Label>
              <KeywordInputRow>
                <Input
                  id="keywordInput"
                  placeholder="익스텐션 검색 키워드 입력"
                  value={keywordText}
                  onChange={(e) => setKeywordText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddKeyword();
                    }
                  }}
                />
                <Button type="button" $secondary onClick={handleAddKeyword}>
                  추가
                </Button>
              </KeywordInputRow>
            </FormRow>

            {keywords.length > 0 && (
              <FormRow $top>
                <Label />
                <KeywordList>
                  {keywords.map((item) => (
                    <KeywordItem key={item} onClick={() => handleRemoveKeyword(item)}>
                      {item}
                    </KeywordItem>
                  ))}
                </KeywordList>
              </FormRow>
            )}

            <FormRow>
              <Label>썸네일 이미지</Label>
              <KeywordInputRow>
                <Input placeholder="파일 이름" readOnly value={thumbnailImage?.name || ''} />
                <Button
                  type="button"
                  $secondary
                  onClick={() => thumbnailInputRef.current && thumbnailInputRef.current.click()}
                >
                  파일 선택
                </Button>
                <HiddenInput type="file" ref={thumbnailInputRef} onChange={handleThumbnailSelect} />
              </KeywordInputRow>
            </FormRow>

            {thumbnailImage && (
              <FormRow $top>
                <Label />
                <PreviewWrapper>
                  <PreviewCard onClick={handleRemoveThumbnail}>
                    <PreviewImage src={thumbnailImage.preview} alt="thumbnail" />
                  </PreviewCard>
                </PreviewWrapper>
              </FormRow>
            )}

            <FormRow>
              <Label>배너 이미지 묶음</Label>
              <KeywordInputRow>
                <Input placeholder="파일 이름" readOnly value="" />
                <Button
                  type="button"
                  $secondary
                  onClick={() => imagesInputRef.current && imagesInputRef.current.click()}
                >
                  파일 추가
                </Button>
                <HiddenInput type="file" ref={imagesInputRef} onChange={handleImagesSelect} />
              </KeywordInputRow>
            </FormRow>

            {detailImages.length > 0 && (
              <FormRow $top>
                <Label />
                <MultiPreviewGrid>
                  {detailImages.map((image, index) => (
                    <PreviewCard key={`${image.name}-${index}`} onClick={() => handleRemoveDetailImage(index)}>
                      <PreviewImage src={image.preview} alt={`detail-${index}`} />
                    </PreviewCard>
                  ))}
                </MultiPreviewGrid>
              </FormRow>
            )}

            <SubmitButton type="button" onClick={submitExtension}>
              익스텐션 추가
            </SubmitButton>
          </FormGrid>
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>익스텐션 코드 등록</SectionTitle>
            <SectionDescription>생성된 익스텐션 ID에 코드를 연결합니다.</SectionDescription>
          </SectionHeader>

          <FormGrid>
            <FormRow>
              <Label htmlFor="extensionId">익스텐션 아이디</Label>
              <Input
                id="extensionId"
                placeholder="익스텐션 아이디 입력"
                value={extensionId}
                onChange={(e) => setExtensionId(e.target.value)}
              />
            </FormRow>

            <FormRow>
              <Label htmlFor="codeType">익스텐션 타입</Label>
              <Select
                id="codeType"
                value={codeType}
                onChange={(e) => setCodeType(e.target.value)}
              >
                <option hidden>선택</option>
                <option value="free">무료</option>
                <option value="block">블록</option>
                <option value="timer">타이머</option>
                <option value="edit">입력폼</option>
                <option value="popup">팝업</option>
                <option value="floating">플로팅 버튼</option>
                <option value="rolling_banner">롤링 배너</option>
              </Select>
            </FormRow>

            <FormRow>
              <Label htmlFor="codeName">코드 이름</Label>
              <Input
                id="codeName"
                placeholder="코드 이름을 입력하세요"
                value={codeName}
                onChange={(e) => setCodeName(e.target.value)}
              />
            </FormRow>

            <FormRow $top>
              <Label htmlFor="extensionCode" $top>익스텐션 코드</Label>
              <Textarea
                id="extensionCode"
                $large
                placeholder="익스텐션 코드를 입력하세요"
                value={extensionCode}
                onChange={(e) => setExtensionCode(e.target.value)}
              />
            </FormRow>

            <SubmitButton type="button" onClick={submitCode}>
              익스텐션 코드 추가
            </SubmitButton>
          </FormGrid>
        </Section>
      </ContentWrapper>
    </PageContainer>
  );
}

export default ExtensionPost;


